import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip, Typography } from 'antd';
import '../InitialMenu.css';
import {
  CameraOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FileOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { GiWeight } from 'react-icons/gi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/common/security/AuthContext';
import { canActOnPage, isPathAllowedForRole } from '../../utils/permissions';

const { Title, Text } = Typography;

const RGiWeight: React.FC<any> = (props) =>
  React.createElement(GiWeight as unknown as React.ComponentType<any>, props) as React.ReactElement;

interface GeneralTile {
  path: string;
  titleKey: string;
  hintKey: string;
  color: string;
  icon: React.ReactNode;
}

const TILES: GeneralTile[] = [
  {
    path: '/core/general/currencies/list',
    titleKey: 'routes.core.currencies',
    hintKey: 'coreGeneral.currenciesHint',
    color: '#1677ff',
    icon: <DollarOutlined />,
  },
  {
    path: '/core/general/units/list',
    titleKey: 'routes.core.units',
    hintKey: 'coreGeneral.unitsHint',
    color: '#722ed1',
    icon: <RGiWeight />,
  },
  {
    path: '/core/general/countries/list',
    titleKey: 'routes.core.countries',
    hintKey: 'coreGeneral.countriesHint',
    color: '#13c2c2',
    icon: <EnvironmentOutlined />,
  },
  {
    path: '/core/general/food-ingredients/list',
    titleKey: 'routes.core.foodIngredients',
    hintKey: 'coreGeneral.foodIngredientsHint',
    color: '#fa8c16',
    icon: <FileTextOutlined />,
  },
  {
    path: '/core/general/professions/list',
    titleKey: 'routes.core.professions',
    hintKey: 'coreGeneral.professionsHint',
    color: '#52c41a',
    icon: <UserOutlined />,
  },
  {
    path: '/core/general/pdf-to-react',
    titleKey: 'routes.core.pdfToReact',
    hintKey: 'coreGeneral.pdfToReactHint',
    color: '#eb2f96',
    icon: <FileTextOutlined />,
  },
  {
    path: '/core/general/pdf-to-svg',
    titleKey: 'routes.core.pdfToSvg',
    hintKey: 'coreGeneral.pdfToSvgHint',
    color: '#2f54eb',
    icon: <FileOutlined />,
  },
  {
    path: '/core/general/pdf-text-extract',
    titleKey: 'routes.core.pdfTextExtract',
    hintKey: 'coreGeneral.pdfTextExtractHint',
    color: '#fa541c',
    icon: <FileSearchOutlined />,
  },
  {
    path: '/core/general/exif-reader',
    titleKey: 'routes.core.exifReader',
    hintKey: 'coreGeneral.exifReaderHint',
    color: '#08979c',
    icon: <CameraOutlined />,
  },
];

const CorGeneral: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { page_permissions, permitted_pages, role } = useAuth();

  const tiles = TILES.filter((tile) =>
    isPathAllowedForRole(tile.path, role) &&
    canActOnPage(page_permissions, permitted_pages, tile.path, 'read')
  );

  return (
    <div style={{ padding: '8px 8px 24px' }}>
      <Title level={3} style={{ marginBottom: 4 }}>{t('coreGeneral.title')}</Title>
      <Text type="secondary">{t('coreGeneral.subtitle')}</Text>
      {tiles.length === 0 ? (
        <div style={{ marginTop: 32 }}>
          <Text type="secondary">{t('coreGeneral.empty')}</Text>
        </div>
      ) : (
        <div className="cc-modules-grid" style={{ marginTop: 24 }}>
          {tiles.map((tile) => (
            <Tooltip key={tile.path} title={t(tile.hintKey)}>
              <div
                className="cc-module-card"
                style={{ '--module-color': tile.color } as React.CSSProperties}
                onClick={() => navigate(tile.path)}
              >
                <div className="cc-module-icon" style={{ color: tile.color }}>
                  {tile.icon}
                </div>
                <div className="cc-module-label">{t(tile.titleKey)}</div>
              </div>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
};

export default CorGeneral;
